import type { BotStrategyConfiguration, TradeResultSyncConfig } from 'api/elplan/elplan.types'
import { formatHHmm } from 'helpers/dateTime.helper/dateTime.helper'
import type { ReactNode } from 'react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { colors } from 'styles/variables'
import Card from 'ui/atoms/Card/Card'
import NewButton from 'ui/atoms/NewButton/NewButton'
import { UnderlineTabs } from 'ui/atoms/Tabs/UnderlineTabs'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { BidSubmitConfirmDialog } from 'ui/components/BidSubmitConfirmDialog/BidSubmitConfirmDialog'
import Datetime from 'utils/datetime/datetime'
import { BidInputTable } from './BidInputTable'
import { BotStrategyTable } from './BotStrategyTable'
import { useBotStrategyExportBehavior } from './hooks/useBotStrategyExportBehavior'
import { useNewerSubmissionWarning } from './hooks/useNewerSubmissionWarning'
import { usePrefilledRowCount } from './hooks/usePrefilledRowCount'
import { useRowCount } from './hooks/useRowCount'
import { useTradeResultSyncBehavior } from './hooks/useTradeResultSyncBehavior'
import { NewerSubmissionWarningDialog } from './NewerSubmissionWarningDialog'

type IntradayBidPositionsPayload = Array<{
  position_id: string
  tags: Array<{
    tag_id: string
    buy?: Array<{ time: ISODateTime; value: number }>
    sell?: Array<{ time: ISODateTime; value: number }>
  }>
}>

type IntradayBidInputCardProps = {
  configuredPositions: Array<{
    position_id: string
    display_name?: string
    tags: Array<{
      tag_id: string
      display_name: string
      enabled?: boolean
      buy: Record<string, unknown>
      sell: Record<string, unknown>
    }>
  }>
  isConfigLoading: boolean
  hasConfigError: boolean
  hasIntradayConfig: boolean
  isSubmitting: boolean
  onSubmit: (positions: IntradayBidPositionsPayload) => Promise<number | undefined>
  activeSubtype?: string
  systemId?: number
  tradeResultSyncConfig?: TradeResultSyncConfig
  showBotStrategySetting?: boolean
  botStrategyConfig?: BotStrategyConfiguration
}

export function BidInputCard({
  configuredPositions,
  isConfigLoading,
  hasConfigError,
  hasIntradayConfig,
  isSubmitting,
  onSubmit,
  activeSubtype,
  systemId,
  tradeResultSyncConfig,
  showBotStrategySetting,
  botStrategyConfig,
}: IntradayBidInputCardProps): ReactNode {
  const { t } = useTranslation()
  const { activeSystem, user } = useAuth()
  const isSuperUser = user?.is_superuser === true
  const [canSubmit, setCanSubmit] = useState(false)
  const [disabledReason, setDisabledReason] = useState('')
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'bid-input' | 'bot-strategy'>('bid-input')
  const [bidBaseStartTime] = useState<Date>(() => new Date(Datetime.getISONow(1, true)))
  const payloadRef = useRef<IntradayBidPositionsPayload>([])
  const hasBotStrategyTable = showBotStrategySetting === true && (botStrategyConfig?.series?.length ?? 0) > 0
  const hasBidInput = hasIntradayConfig && !isConfigLoading && !hasConfigError
  const { isSubmitPending, submitOrWarn, bidTableLoad, warningDialog } = useNewerSubmissionWarning({
    systemId,
    bidBaseStartTime,
    hasBidInput,
    submit: () => onSubmit(payloadRef.current),
  })
  const prefilledRowCount = usePrefilledRowCount({
    systemId,
    bidBaseStartTime,
    hasBidInput,
    hasBotStrategy: hasBotStrategyTable,
    configuredPositions,
    activeSubtype,
    botStrategySeries: botStrategyConfig?.series ?? [],
  })
  const { rowCount, handleAddRow, handleRemoveLastRow } = useRowCount(prefilledRowCount)

  const {
    isSyncing,
    lastSyncTimeData,
    lastFetchedAt,
    isTradeResultFetching,
    handleSyncClick,
    handleRefreshClick,
    handleTradeResultFetched,
    handleTradeResultFetchingChange,
  } = useTradeResultSyncBehavior(systemId, activeSubtype, tradeResultSyncConfig)

  const { isExporting, handleBotStrategyValuesChange, handleExportBotStrategySetting } = useBotStrategyExportBehavior(
    systemId,
    botStrategyConfig,
    bidBaseStartTime,
    rowCount
  )

  const handleEligibilityChange = (eligibility: { canSubmit: boolean; disabledReason: string }) => {
    setCanSubmit((previous) => (previous === eligibility.canSubmit ? previous : eligibility.canSubmit))
    setDisabledReason((previous) => (previous === eligibility.disabledReason ? previous : eligibility.disabledReason))
  }

  const handlePayloadChange = (positions: IntradayBidPositionsPayload) => {
    payloadRef.current = positions
  }

  const handleSubmit = () => {
    if (!hasBidInput) {
      return
    }

    void submitOrWarn()
  }

  const handleSubmitClick = () => {
    if (isSuperUser && activeSystem) {
      setIsConfirmDialogOpen(true)
    } else {
      handleSubmit()
    }
  }

  const submitDisabledReason =
    (isConfigLoading && t('Intraday configuration is loading.')) ||
    (hasConfigError && t('Failed to load intraday configuration.')) ||
    (!hasIntradayConfig && t('Intraday bid submission is not configured for this system yet.')) ||
    disabledReason

  const canSubmitFromState = hasBidInput && canSubmit

  return (
    <Card title={t('Enter bids')} titleSize="medium" collapsible>
      {showBotStrategySetting && (
        <UnderlineTabs
          items={[
            { id: 'bid-input', label: t('Bid input') },
            { id: 'bot-strategy', label: t('Bot strategy setting') },
          ]}
          activeId={activeTab}
          onSelect={setActiveTab}
        />
      )}
      <div
        role={showBotStrategySetting ? 'tabpanel' : undefined}
        id={showBotStrategySetting ? 'tabpanel-bid-input' : undefined}
        aria-labelledby={showBotStrategySetting ? 'tab-bid-input' : undefined}
        hidden={showBotStrategySetting && activeTab !== 'bid-input'}
      >
        {isConfigLoading ? (
          <p>{t('Loading intraday configuration...')}</p>
        ) : hasConfigError ? (
          <p>{t('Failed to load intraday configuration. Please try again later.')}</p>
        ) : hasIntradayConfig ? (
          <BidInputTable
            key={bidTableLoad}
            configuredPositions={configuredPositions}
            onEligibilityChange={handleEligibilityChange}
            onPayloadChange={handlePayloadChange}
            activeSubtype={activeSubtype}
            systemId={systemId}
            tradeResultSyncConfig={tradeResultSyncConfig}
            onTradeResultFetched={tradeResultSyncConfig !== undefined ? handleTradeResultFetched : undefined}
            onTradeResultFetchingChange={
              tradeResultSyncConfig !== undefined ? handleTradeResultFetchingChange : undefined
            }
            bidBaseStartTime={bidBaseStartTime}
            rowCount={rowCount}
            onAddRow={handleAddRow}
            onRemoveLastRow={handleRemoveLastRow}
            topRightAction={
              tradeResultSyncConfig !== undefined ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                  <NewButton
                    variant="outlined"
                    label={t('Fetch latest traded volume')}
                    onClick={handleRefreshClick}
                    disabled={!systemId || isTradeResultFetching}
                    loading={isTradeResultFetching}
                  />
                  {lastFetchedAt !== null && (
                    <span
                      style={{ fontSize: '0.75rem', color: colors.grey7, whiteSpace: 'nowrap', marginRight: '10px' }}
                    >
                      {lastFetchedAt.toDateString() === new Date().toDateString()
                        ? t('Last fetched: {{time}}', { time: formatHHmm(lastFetchedAt) })
                        : t('Last fetched: {{date}} {{time}}', {
                            date: lastFetchedAt.toLocaleDateString(),
                            time: formatHHmm(lastFetchedAt),
                          })}
                    </span>
                  )}
                </div>
              ) : undefined
            }
            secondaryAction={
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <NewButton
                  variant="primary"
                  label={isSubmitting || isSubmitPending ? t('Submitting...') : t('Submit positions')}
                  onClick={handleSubmitClick}
                  disabled={!canSubmitFromState || isSubmitting || isSubmitPending}
                  loading={isSubmitting || isSubmitPending}
                  tooltip={
                    submitDisabledReason
                      ? {
                          title: submitDisabledReason,
                          placement: 'top',
                        }
                      : undefined
                  }
                />
                {tradeResultSyncConfig !== undefined && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                    <NewButton
                      variant="primary"
                      label={t('Accept traded volume')}
                      onClick={handleSyncClick}
                      disabled={!systemId || isSyncing}
                      loading={isSyncing}
                    />
                    {lastSyncTimeData?.synced_at != null &&
                      (() => {
                        const syncedAt = new Date(lastSyncTimeData.synced_at)
                        return (
                          <span
                            style={{
                              fontSize: '0.75rem',
                              color: colors.grey7,
                              whiteSpace: 'nowrap',
                              marginRight: '10px',
                            }}
                          >
                            {syncedAt.toDateString() === new Date().toDateString()
                              ? t('Last accepted: {{time}}', { time: formatHHmm(syncedAt) })
                              : t('Last accepted: {{date}} {{time}}', {
                                  date: syncedAt.toLocaleDateString(),
                                  time: formatHHmm(syncedAt),
                                })}
                          </span>
                        )
                      })()}
                  </div>
                )}
              </div>
            }
          />
        ) : (
          <p>{t('Intraday bid submission is not configured for this system yet.')}</p>
        )}
      </div>
      {showBotStrategySetting && (
        <div
          role="tabpanel"
          id="tabpanel-bot-strategy"
          aria-labelledby="tab-bot-strategy"
          hidden={activeTab !== 'bot-strategy'}
        >
          {isConfigLoading ? (
            <p>{t('Loading intraday configuration...')}</p>
          ) : hasConfigError ? (
            <p>{t('Failed to load intraday configuration. Please try again later.')}</p>
          ) : botStrategyConfig != null && (botStrategyConfig.series?.length ?? 0) > 0 ? (
            <BotStrategyTable
              key={systemId}
              series={botStrategyConfig.series}
              bidBaseStartTime={bidBaseStartTime}
              systemId={systemId}
              onValuesChange={handleBotStrategyValuesChange}
              rowCount={rowCount}
              onAddRow={handleAddRow}
              onRemoveLastRow={handleRemoveLastRow}
            />
          ) : (
            <p>{t('Bot strategy is not configured for this system.')}</p>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
            <NewButton
              variant="primary"
              label={t('Export bot strategy setting')}
              onClick={handleExportBotStrategySetting}
              disabled={
                !systemId ||
                isExporting ||
                isConfigLoading ||
                hasConfigError ||
                !botStrategyConfig ||
                (botStrategyConfig.series?.length ?? 0) === 0
              }
              loading={isExporting}
            />
          </div>
        </div>
      )}
      {activeSystem && (
        <BidSubmitConfirmDialog
          open={isConfirmDialogOpen}
          onClose={() => setIsConfirmDialogOpen(false)}
          onConfirm={() => {
            setIsConfirmDialogOpen(false)
            handleSubmit()
          }}
          systemName={activeSystem.name}
          isPreview={activeSystem.is_preview}
        />
      )}
      <NewerSubmissionWarningDialog {...warningDialog} />
    </Card>
  )
}
